import { Controller } from '@hotwired/stimulus'
import {
  getElementFromEventTarget,
  resolveTargetFromHref,
  scrollToHash,
  HEADER_MAIN_GAP
} from '../utils/anchor_scroll.js'

/** Навигация по якорям страницы: подсветка, липкая панель, Swiper списка на десктопе. */
export default class PageAnchorsController extends Controller {
  #list = null
  #sections = []
  #activeAnchor = null
  #footer = null
  #header = null
  #mediaQuery = window.matchMedia('(min-width: 1024px)')
  #swiper = null
  #stickyTimer = null
  #isInitializing = false

  connect() {
    this.#list = this.element.querySelector('.page-anchors__list')
    if (!this.#list) return

    this.#list.querySelectorAll('a').forEach((anchor) => {
      const href = anchor.getAttribute('href')
      const section = href ? resolveTargetFromHref(href) : null
      if (section) this.#sections.push(section)
    })
    this.#sections.sort((a, b) => a.offsetTop - b.offsetTop)

    this.#footer = document.querySelector('footer')
    this.#header = document.querySelector('header[data-controller~="header"]')

    document.addEventListener('scroll', this.#onScroll, { passive: true })
    this.#list.addEventListener('click', this.#onAnchorClick)

    this.#mediaQuery.addEventListener('change', this.#checkWidth)
    this.#checkWidth()
  }

  disconnect() {
    document.removeEventListener('scroll', this.#onScroll)
    this.#list?.removeEventListener('click', this.#onAnchorClick)
    clearTimeout(this.#stickyTimer)
    this.#swiper?.destroy(true, true)
    this.#swiper = null
    this.#sections = []
    this.#activeAnchor = null
    this.#setSticky(false)
  }

  // ─── Приватные методы ─────────────────────────────────────────────────────
  // Сортировка секций и обновление «десктопности»: Swiper только при ширине ≥ 1024px.

  #checkWidth = () => {
    this.#sections.sort((a, b) => a.offsetTop - b.offsetTop)

    const isDesktop = this.#mediaQuery.matches
    const isOverflowing = this.#list.scrollWidth > this.#list.clientWidth
    this.element.classList.toggle('is-draggable', isOverflowing && isDesktop)

    if (this.#swiper) {
      this.#swiper?.destroy(true, true)
      this.#swiper = null
    }

    this.#initSwiper()
  }

  // Активный пункт по позиции скролла; липкая панель у футера
  #onScroll = () => {
    const scrollTop = document.documentElement.scrollTop
    const headerHeight = this.#header?.offsetHeight ?? 0
    const offset = headerHeight + this.element.offsetHeight + HEADER_MAIN_GAP

    let active = null
    for (let i = this.#sections.length - 1; i >= 0; i--) {
      if (this.#sections[i].offsetTop - offset <= scrollTop) {
        active = this.#sections[i]
        break
      }
    }

    if (active) {
      const anchor = this.#list.querySelector(
        `a[href="#${CSS.escape(active.id)}"]`
      )
      if (anchor) this.#setActiveAnchor(anchor)
    }

    const footerTop = this.#footer?.getBoundingClientRect().top ?? 0
    this.#setSticky(
      this.element.getBoundingClientRect().bottom < 0 && footerTop > 0
    )
  }

  // Прикрепить якоря к верху страницы и сжать шапку
  #setSticky(need) {
    clearTimeout(this.#stickyTimer)
    if (need) {
      this.element.classList.add('is-sticky')
      this.#stickyTimer = setTimeout(
        () => this.element.classList.add('is-visible'),
        300
      )
    } else {
      this.element.classList.remove('is-visible', 'is-sticky')
    }
  }

  // Подсветка якоря только если целевая секция есть в DOM
  #onAnchorClick = (event) => {
    const eventTarget = getElementFromEventTarget(event.target)
    const target = eventTarget?.closest('a')
    if (!target) return

    const href = target.getAttribute('href')
    if (!href || !resolveTargetFromHref(href)) return

    event.preventDefault()
    if (scrollToHash(href, { behavior: 'smooth' })) {
      this.#setActiveAnchor(target)
    }
  }

  #setActiveAnchor(anchor) {
    if (this.#activeAnchor === anchor) return
    this.#activeAnchor?.classList.remove('is-active')
    this.#activeAnchor = anchor
    anchor.classList.add('is-active')

    this.#scrollActiveAnchorIntoView(anchor)
  }

  // Прокручиваем только локальный контейнер якорей, не страницу.
  #scrollActiveAnchorIntoView(anchor) {
    if (this.#swiper) {
      this.#scrollActiveAnchorWithSwiper(anchor)
    }
  }

  #scrollActiveAnchorWithSwiper(anchor) {
    const slide = anchor.closest('.swiper-slide')
    const index = slide ? Array.from(this.#swiper.slides).indexOf(slide) : -1
    if (index >= 0) this.#swiper.slideTo(index)
  }

  // Защита от параллельной инициализации и ошибок динамического импорта
  async #initSwiper() {
    if (this.#swiper || this.#isInitializing) return
    this.#isInitializing = true

    try {
      const [{ default: Swiper }, { FreeMode, Mousewheel }] = await Promise.all(
        // eslint-disable-next-line import/no-unresolved
        [import('swiper'), import('swiper/modules')]
      )

      if (!this.#list) return // контроллер отключён или ресайз на мобильный пока ждали импорт

      if (!this.#list.querySelector('.swiper-wrapper')) {
        const wrapper = document.createElement('div')
        wrapper.classList.add('swiper-wrapper')
        Array.from(this.#list.children).forEach((child) => {
          const slide = document.createElement('div')
          slide.classList.add('swiper-slide')
          slide.appendChild(child)
          wrapper.appendChild(slide)
        })
        this.#list.appendChild(wrapper)
      }

      this.#swiper = new Swiper(this.#list, {
        modules: [FreeMode, Mousewheel],
        speed: 300,
        slidesPerView: 'auto',
        slidesPerGroup: 2,
        spaceBetween: 0,
        freeMode: true,
        mousewheel: { enabled: true, releaseOnEdges: true }
      })
    } catch (e) {
      console.error('[PageAnchors] Swiper init failed', e)
    } finally {
      this.#isInitializing = false
    }
  }
}
